<script setup lang="ts">
  import RecipientCard from '@/components/drawer/DrawerRecipients/RecipientCard.vue'
  import RoleAdder from './RoleAdder.vue'
  import { ADD_ROLE_OPTION_VALUE, useGroups, useRecipients, useRoleModal } from '@/composition'
  import { useBuilderStore } from '@/store/builder'
  import { LayerSingleIcon, LayersThree02Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { GROUP_META, groupRuleOptions, groupRuleValueOptions, renderLabelAvatarOnSelect } from '@gohighlevel/ghl-proposals-common'
  import { UIButton, UIDivider, UIFormItem, UISelect } from '@gohighlevel/ghl-ui'
  import { computed, onMounted, ref } from 'vue'
  const { syncRecipient } = useBuilderStore()
  const groupRuleOptionsComputed = groupRuleOptions()
  const { groupTargets, unGroupTargets, groupExist, updateGroupOptions } = useGroups()
  const {
    signedByOptionsWithStaff,
    assignRecipientsToCheckBoxGroup,
    fetchRecipientForElementOrGroup,
    getSelectValue,
    isContentLibraryTemplate,
  } = useRecipients()
  const searchQuery = ref('')
  const { showAddRoleModal, roleToEdit, openAddRole, openEditRole } = useRoleModal()
  const selectValue = computed(() => {
    if (groupExist.value) return getSelectValue(groupExist.value)
    return ''
  })
  const recipient = computed(() => {
    if (groupExist.value) {
      return fetchRecipientForElementOrGroup(groupExist.value)
    }
    return ''
  })
  const assignRecipientToCheckBoxGroup = (val: string, groupId: string) => {
    if (val === ADD_ROLE_OPTION_VALUE) {
      openAddRole()
      syncRecipientList()
      return
    }
    if (val && groupId) {
      assignRecipientsToCheckBoxGroup(val, groupId)
      syncRecipientList()
    }
  }
  const syncRecipientList = () => {
    syncRecipient()
  }
  onMounted(() => {
    syncRecipientList()
  })

  const filteredSignedByOptions = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return signedByOptionsWithStaff.value
    }
    const query = searchQuery.value.toLowerCase().trim()
    return signedByOptionsWithStaff.value.filter((option: any) => {
      const label = option.label?.toLowerCase() || ''
      return label.includes(query)
    })
  })

  const handleSearch = (query: string) => {
    searchQuery.value = query
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content hl-proposal-checkbox-properties">
    <div class="my-6">
      <div v-if="groupExist">
        <UIButton
          @click="unGroupTargets"
          :class="'group-field-button'"
          :id="`grouping`"
          :type="'default'"
          :size="`large`"
          :disabled="false"
          :loading="false"
          :ghost="false"
          :quaternary="false"
          :circle="false"
          :text="false"
        >
          <LayerSingleIcon class="w-4 h-4 mr-2" />
          {{ $t('common.ungroupCheckbox') }}
        </UIButton>
      </div>

      <div v-else>
        <UIButton
          @click="groupTargets"
          :class="'group-field-button'"
          :id="`grouping`"
          :type="'default'"
          :size="`large`"
          :disabled="false"
          :loading="false"
          :ghost="false"
          :quaternary="false"
          :circle="false"
          :text="false"
        >
          <LayersThree02Icon class="w-4 h-4 mr-2" />
          {{ $t('common.groupCheckbox') }}
        </UIButton>
      </div>
    </div>
    <div class="my-6" v-if="groupExist">
      <UIDivider />
      <div class="mb-4">
        <UIFormItem :label="$t('common.toBeFilledBy')">
          <UISelect
            :id="`checkbox-group-text`"
            :placeholder="$t('common.assignRecipientToCheckbox')"
            :options="filteredSignedByOptions"
            :value="selectValue"
            filterable
            :render-label="renderLabelAvatarOnSelect"
            @update:value="(val: any) => assignRecipientToCheckBoxGroup(val, groupExist.id)"
            @search="handleSearch"
            :disabled="isContentLibraryTemplate"
          />
        </UIFormItem>
        <RoleAdder
          v-model:show="showAddRoleModal"
          :role="roleToEdit"
          @added="(val: string) => assignRecipientToCheckBoxGroup(val, groupExist?.id as string)"
        />
        <div>
          <RecipientCard :recipient="recipient" @edit-role="openEditRole" />
        </div>
      </div>
      <UIDivider />
      <label class="mt-5" :for="`${groupExist?.id}-checkbox-group-rule-name`">
        {{ $t('validations.required.requiredCheckboxes') }}
      </label>
      <div class="flex space-x-4">
        <div class="w-2/3">
          <UISelect
            :id="`${groupExist?.id}-checkbox-group-rule-name`"
            :options="groupRuleOptionsComputed"
            :type="'default'"
            :reset-menu-on-options-change="true"
            :size="'large'"
            :max-tag-count="undefined"
            :value="groupExist.component.options.rule"
            @update:value="val => updateGroupOptions(GROUP_META.RULE, val, groupExist?.id)"
          />
        </div>
        <div class="w-1/3">
          <UISelect
            :id="`${groupExist?.id}-checkbox-group-rule-value`"
            :options="groupRuleValueOptions(groupExist)"
            :type="'default'"
            :reset-menu-on-options-change="true"
            :size="'large'"
            :value="groupExist.component.options.ruleValue"
            @update:value="val => updateGroupOptions(GROUP_META.RULE_VALUE, val, groupExist?.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .group-field-button {
    width: 100%;
    border: 1px solid #eff4ff;
  }
  .hl-proposal-checkbox-properties {
    .n-divider.n-divider--no-title {
      margin: 20px 0;
    }
  }
</style>
